import * as React from 'react';
import styled from 'styled-components';
import { CollapsiblePanel } from '../../../designSystem/collapsible/CollapsiblePanel';
import { RadialPctChart } from '../../../designSystem/graphs/RadialPctChart';
import { InfoChip } from '../../../designSystem/Chip';

export type GroupedQuestionAnswerItem = {
    questionIndex: number;
    questionText: string;
    answerText: string;
    ratingPct?: number;
    benchmarkRatingPct?: number;
};

export type GroupedQuestionAnswer = {
    label: string;
    items: GroupedQuestionAnswerItem[];
};

type QuestionAnswerListProps = {
    groups: GroupedQuestionAnswer[];
};

function groupAvgRating(group: GroupedQuestionAnswer): number | undefined {
    const rated = group.items.filter(i => typeof i.ratingPct === 'number');
    if (rated.length === 0) return undefined;
    return Math.round(rated.reduce((sum, i) => sum + (i.ratingPct ?? 0), 0) / rated.length);
}

export function QuestionAnswerList(props: QuestionAnswerListProps): JSX.Element {
    return (
        <OverviewSection>
            {props.groups.length === 0 && (
                <Text>Der er endnu ingen registrerede svar.</Text>
            )}

            {props.groups.length > 0 && (
                <AnswerGroupList>
                    {props.groups.map(group => (
                        <AnswerGroup key={group.label}>
                            <CollapsiblePanel
                                defaultOpen={true}
                                closedOrDefaultHeader={
                                    <AnswerGroupHeader>
                                        <AnswerGroupTitleRow>
                                            <AnswerGroupTitle>{group.label}</AnswerGroupTitle>
                                            {groupAvgRating(group) !== undefined && (
                                                <RadialPctChart pct={groupAvgRating(group) ?? 0} dim='60px' fontSize='10px' />
                                            )}
                                        </AnswerGroupTitleRow>
                                        <InfoChip>
                                            <AnswerGroupCount>{group.items.length} spørgsmål</AnswerGroupCount>
                                        </InfoChip>
                                    </AnswerGroupHeader>
                                }>
                                <AnswerList>
                                    <AnswerListHeader>
                                        <HeaderCell>Spørgsmål</HeaderCell>
                                        <HeaderCell>Svar</HeaderCell>
                                        <HeaderCell align='right'>Score</HeaderCell>
                                        <HeaderCell align='right'>Benchmark</HeaderCell>
                                    </AnswerListHeader>

                                    {group.items.map(item => (
                                        <AnswerItem key={item.questionIndex}>
                                            <AnswerQuestion>
                                                {item.questionIndex + 1}. {item.questionText}
                                            </AnswerQuestion>
                                            <AnswerValue>{item.answerText}</AnswerValue>
                                            <ScoreCell>
                                                {typeof item.ratingPct === 'number'
                                                    ? <RadialPctChart pct={item.ratingPct} dim='46px' fontSize='9px' />
                                                    : <ScoreFallback>-</ScoreFallback>}
                                            </ScoreCell>
                                            <ScoreCell>
                                                {typeof item.benchmarkRatingPct === 'number'
                                                    ? <RadialPctChart pct={item.benchmarkRatingPct} dim='46px' fontSize='9px' />
                                                    : <ScoreFallback>-</ScoreFallback>}
                                            </ScoreCell>
                                        </AnswerItem>
                                    ))}
                                </AnswerList>
                            </CollapsiblePanel>
                        </AnswerGroup>
                    ))}
                </AnswerGroupList>
            )}
        </OverviewSection>
    );
}

const OverviewSection = styled.section`
    margin-top: 22px;
`;

const Text = styled.p`
    margin: 0 0 8px;
    color: #5e6f81;
`;

const AnswerGroupList = styled.div`
    display: grid;
    gap: 10px;
`;

const AnswerGroup = styled.section`
    border: 1px solid #dce5ef;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(17, 54, 91, 0.05);
`;

const AnswerGroupHeader = styled.div`
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
`;

const AnswerGroupTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const AnswerGroupTitle = styled.span`
    font-weight: 600;
    color: #1f2f40;
`;

const AnswerGroupCount = styled.span`
    color: #607182;
    font-size: 0.8rem;
    font-weight: 600;
`;

const AnswerList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0 12px 12px;
    display: grid;
    gap: 8px;
`;

const AnswerListHeader = styled.li`
    display: grid;
    grid-template-columns: minmax(260px, 1.8fr) minmax(160px, 1fr) 70px 90px;
    gap: 8px;
    padding: 0 10px;

    @media (max-width: 900px) {
        display: none;
    }
`;

const HeaderCell = styled.p<{ align?: 'left' | 'right' }>`
    margin: 0;
    font-size: 0.78rem;
    color: #607182;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-align: ${props => props.align || 'left'};
`;

const AnswerItem = styled.li`
    border: 1px solid #dfe7f0;
    border-radius: 10px;
    background: #fbfdff;
    padding: 10px;
    display: grid;
    grid-template-columns: minmax(260px, 1.8fr) minmax(160px, 1fr) 70px 90px;
    gap: 8px;
    align-items: center;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        align-items: start;
        gap: 6px;
    }
`;

const AnswerQuestion = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 400;

    @media (max-width: 900px) {
        white-space: normal;
    }
`;

const AnswerValue = styled.p`
    margin: 0;
    color: #11365b;    
    font-size: 14px;
    font-weight: 400;

    @media (max-width: 900px) {
        white-space: normal;
    }
`;

const ScoreCell = styled.div`
    display: grid;
    justify-items: end;

    @media (max-width: 900px) {
        justify-items: start;
    }
`;

const ScoreFallback = styled.p`
    margin: 0;
    font-weight: 700;
    color: #2f3f50;
`;
